/**
 * Onboarding route (APP-002 §5, §6, §30).
 *
 * Drives the multi-step onboarding flow: welcome → account_setup (optional) →
 * orientation → next_action. Tracks step state, announces progress, persists
 * completion via the safe local marker, and redirects returning (completed)
 * users to /dashboard.
 *
 * The route runs INSIDE the protected shell, so the backend account is already
 * resolved (ready) when this mounts — onboarding is a UI convenience layered
 * on top of authoritative account state, never a substitute for it (APP-002 §6).
 */
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDashboardIdentity } from '@/layouts/DashboardLayout';
import { track } from '@/lib/analytics';
import { sanitizeReturnPath } from '@/lib/redirect';
import { PageContainer } from '@/components/navigation/PageContainer';
import { Breadcrumbs } from '@/components/navigation/Breadcrumbs';
import { PageHeader } from '@/components/navigation/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowRight } from '@/components/icons';
import { OnboardingProgress } from './OnboardingProgress';
import { WelcomeStep } from './steps/WelcomeStep';
import { AccountSetupStep } from './steps/AccountSetupStep';
import { OrientationStep } from './steps/OrientationStep';
import { NextActionStep } from './steps/NextActionStep';
import { useOnboardingCompletion } from './useOnboardingCompletion';
import {
  clearOnboardingStep,
  readOnboardingStep,
  writeOnboardingStep,
} from './onboardingStepStorage';
import {
  ONBOARDING_TOTAL_STEPS,
  nextStepId,
  previousStepId,
  stepOrdinal,
  type OnboardingStepId,
} from './onboardingSteps';

/**
 * Heading per step. The route renders a single changing h1 so screen readers
 * announce the step transition; focus is moved to the new heading on change
 * (APP-002 §26).
 */
const STEP_HEADING: Record<OnboardingStepId, string> = {
  welcome: 'Welcome',
  account_setup: 'Set up your account',
  orientation: 'How it works',
  next_action: 'Get started',
};

export function OnboardingRoute() {
  const identity = useDashboardIdentity();
  const navigate = useNavigate();
  const location = useLocation();
  const completion = useOnboardingCompletion();
  // Resume a persisted step (sessionStorage) so remounts — gate redirect
  // bounces, refreshes, external-navigation round-trips — do not reset the
  // flow to step 1 (APP-002 §30 trap interplay).
  const [step, setStep] = useState<OnboardingStepId>(() => readOnboardingStep() ?? 'welcome');
  const headingRef = useRef<HTMLHeadingElement>(null);

  // All step transitions funnel through changeStep so the persisted mirror
  // never drifts from the rendered step.
  function changeStep(next: OnboardingStepId) {
    setStep(next);
    writeOnboardingStep(next);
  }

  // Validated return path preserved through the gate (APP-002 §30). Only a
  // same-origin application path is ever honored.
  const returnPath = (() => {
    const raw = new URLSearchParams(location.search).get('redirect');
    return raw ? sanitizeReturnPath(raw) : null;
  })();

  // APP-002 §6: returning users with a completed marker go to the dashboard.
  // (The AccountGate also handles this; this is a belt-and-braces guard for
  //  direct navigation to /onboarding while already completed.)
  useEffect(() => {
    track({ event: 'onboarding_started' });
    if (completion.completed) {
      navigate('/dashboard', { replace: true });
    }
  }, [completion.completed, navigate]);

  // Move focus to the new heading on step change (APP-002 §26).
  useEffect(() => {
    headingRef.current?.focus();
  }, [step]);

  function goTo(next: OnboardingStepId) {
    track({ event: 'onboarding_step_completed', step });
    changeStep(next);
  }

  function advance() {
    const next = nextStepId(step);
    if (next) goTo(next);
  }

  function back() {
    const prev = previousStepId(step);
    if (prev) changeStep(prev);
  }

  function skipToDashboard() {
    track({ event: 'onboarding_skipped' });
    completion.markCompleted();
    clearOnboardingStep();
    navigate(returnPath ?? '/dashboard', { replace: true });
  }

  /**
   * Mark onboarding complete without navigating. The final step's action
   * links call this from their onClick — react-router runs it BEFORE the
   * link's client-side navigation, so the account gate admits the user to
   * the chosen route instead of bouncing them back into a remounted
   * onboarding (APP-002 §10 — completion is handled by the parent route).
   */
  function finishInPlace() {
    track({ event: 'onboarding_completed' });
    completion.markCompleted();
    clearOnboardingStep();
  }

  function finish() {
    finishInPlace();
    navigate(returnPath ?? '/dashboard', { replace: true });
  }

  const ordinal = stepOrdinal(step);
  const isLast = step === 'next_action';
  const isFirst = step === 'welcome';

  return (
    <PageContainer>
      <Breadcrumbs items={[{ label: 'Onboarding' }]} />
      <PageHeader
        title="Onboarding"
        description={`Step ${ordinal} of ${ONBOARDING_TOTAL_STEPS} — get set up in a few minutes.`}
        actions={
          !isFirst ? (
            <Button size="sm" variant="ghost" onClick={skipToDashboard}>
              Skip for now
            </Button>
          ) : null
        }
      />

      <div className="mb-6">
        <OnboardingProgress current={step} />
      </div>

      <Card>
        <CardContent className="p-6 sm:p-8">
          <h2 ref={headingRef} tabIndex={-1} className="sr-only">
            {STEP_HEADING[step]}
          </h2>

          {step === 'welcome' ? <WelcomeStep /> : null}
          {step === 'account_setup' ? (
            <AccountSetupStep
              initialName={identity.account.account_name}
              onSaved={advance}
              onSkip={advance}
            />
          ) : null}
          {step === 'orientation' ? <OrientationStep /> : null}
          {step === 'next_action' ? <NextActionStep onFinish={finish} onComplete={finishInPlace} /> : null}
        </CardContent>
      </Card>

      {/* Step navigation. The account-setup step renders its own controls via
          the form, so generic next/back are hidden there. */}
      <div className="mt-6 flex items-center justify-between">
        {!isFirst && step !== 'account_setup' ? (
          <Button variant="ghost" size="sm" onClick={back}>
            Back
          </Button>
        ) : (
          <span />
        )}
        {!isLast && step !== 'account_setup' ? (
          <Button size="sm" onClick={advance}>
            Continue
            <ArrowRight className="h-4 w-4" />
          </Button>
        ) : null}
      </div>
    </PageContainer>
  );
}
